// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@awc/App/Components/Notify";
import SubmitQuickRfqForm from "@awc/App/Handlers/CustomForms/QuickRfqForm";
import { QuickRfqApiParameter } from "@awc/App/Services/CustomForms";
import { xIsAuthenticated } from "@awc/App/Store/Selectors/GeneralSelectors";
import { isExpeditedForm } from "@awc/App/Store/Selectors/ProductSelectors";
import { getSettingsCollection } from "@awc/App/Store/Selectors/SettingsSelectors";
import { xGoogleAnalyticsState } from "@awc/App/Widgets/GoogleAnalyticsConnector/Store/Reducers";
import { trackHubspotRfqGa4Events } from "@awc/App/Widgets/GoogleAnalyticsConnector/Utilities/DataLayer";
import { DatePickerState } from "@awc/MobiusOverrides/DatePicker";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { applyMask, mergeWithCss, useValidate, xDefaultRules } from "@awc/XDL/Utilities";
import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadBillTo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/LoadBillTo";
import translate from "@insite/client-framework/Translate";
import { CountryModel, TraitValueModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker from "@insite/mobius/DatePicker";
import FileUpload from "@insite/mobius/FileUpload";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, RefObject, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { AnyStyledComponent } from "styled-components";

interface OwnProps {
    extendedStyles?: QuickRfqFromtSyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    countries: getCurrentCountries(state),
    currentBillTo: getCurrentBillToState(state).value,
    session: state.context.session,
    reCaptchaEnabledForQuickRfq: getSettingsCollection(state).xngSecuritySettings.checkReCaptchaForPdpRfqOnClientSide,
    variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
    variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
    variantSelection: state.pages.productDetails.variantSelection,
    thankYouUrl: getPageLinkByPageType(state, "RfqSubmitThankYou")?.url,
    isAuth: xIsAuthenticated(state),
    requestQuoteEvent: (state as xGoogleAnalyticsState).xGoogleAnalytics.requestQuoteEvent,
});

const mapDispatchToProps = {
    SubmitQuickRfqForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    loadCurrentCountries,
    loadBillTo,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasParentProductId;

interface QuickRfqFromtSyles {
    formWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    fileUpload?: InjectableCss;
    title?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
}

export const quickRfqFromStyles: QuickRfqFromtSyles = {};

const StyledForm = getStyledWrapper("form");

const QuickRfqFrom: FC<Props> = ({
    SubmitQuickRfqForm,
    loadCurrentCountries,
    loadBillTo,
    countries,
    reCaptchaEnabledForQuickRfq,
    extendedStyles,
    session,
    currentBillTo,
    variantTraits,
    variantChildren,
    variantSelection,
    productContext: { product, productInfo },
    thankYouUrl,
    isAuth,
    validateReCaptcha,
    requestQuoteEvent,
}) => {
    const [styles] = useState(() => mergeWithCss(quickRfqFromStyles, extendedStyles));
    const [errorMessage, setErrorMessage] = useState("");
    const [country, setCountry] = useState<CountryModel>();
    const [traitValues, setTraitValues] = useState<TraitValueModel[]>([]);
    const [file, setFile] = useState<any>(null);
    const [date, setdate] = useState<Date | undefined>();

    const history = useHistory();

    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const isExpedited = isExpeditedForm(product, productInfo);
    const isVariant = variantTraits && variantTraits.length > 0 && variantChildren;

    const { masksList, setFieldValue, values, field, handleSubmit, isSubmitting, errors } = useValidate({
        initialValues: {
            Length: "",
            FirstName: session.firstName || "",
            LastName: session.lastName || "",
            Email: session.email || "",
            Company: "",
            Phone: "",
            CountryCode: "",
            ErpNumber: product?.productNumber,
            StreetAddress: "",
            City: "",
            State: "",
            PostalCode: "",
            Notes: "",
            AttachmentBase64: "",
            AttachmentFileName: "",
            AttachmentMimeType: "",
            // ExpeditedForm
            ...(isExpedited && { RequiredBy: "" }),
            ...(isVariant && { Color: "" }),
        },
        validators: {
            Length: `required|${xDefaultRules.text}`,
            FirstName: `required|${xDefaultRules.firstName}`,
            LastName: `required|${xDefaultRules.lastName}`,
            Email: `required|${xDefaultRules.email}`,
            Company: `required|${xDefaultRules.companyName}`,
            Phone: `required|${xDefaultRules.phone}`,
            CountryCode: `required|${xDefaultRules.city}`,
            ErpNumber: `required`,
            StreetAddress: `required|${xDefaultRules.address}`,
            City: `required|${xDefaultRules.city}`,
            State: country?.states?.length ? `required|${xDefaultRules.text}` : `${xDefaultRules.text}`,
            PostalCode: `required|${
                country?.abbreviation?.toLowerCase() === "ca"
                    ? "postalCodeCA"
                    : country?.abbreviation?.toLowerCase() === "us"
                    ? "postalCodeUS"
                    : "text"
            }`,
            Notes: xDefaultRules.notes,
            // ExpeditedForm
            ...(isExpedited && { RequiredBy: `required` }),
            ...(isVariant && { Color: `required|${xDefaultRules.text}` }),
        },
        onSubmit: (fieldValues: QuickRfqApiParameter, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    // Load countries if not available and set selected country
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        } else if (isAuth && currentBillTo) {
            // Set Selected Country
            const selectedCountry = countries?.find(c => c.abbreviation === currentBillTo?.country?.abbreviation);
            setCountry(selectedCountry);
            selectedCountry && setFieldValue("CountryCode", selectedCountry?.abbreviation);

            // Set Selected State
            const selectedState = selectedCountry?.states?.find(
                c => c.abbreviation === currentBillTo?.state?.abbreviation,
            );
            selectedState && setFieldValue("State", selectedState?.abbreviation);
        }
    }, [countries, currentBillTo]);

    // Load billing info if missing and set form fields
    useEffect(() => {
        if (!isAuth) {
            return;
        }

        if (!currentBillTo) {
            loadBillTo();
        } else {
            setFieldValue("Company", currentBillTo.companyName);
            setFieldValue("Phone", currentBillTo.phone);
            setFieldValue("City", currentBillTo.city);
            setFieldValue("PostalCode", currentBillTo.postalCode);
            setFieldValue("StreetAddress", currentBillTo.address1);
        }
    }, [currentBillTo]);

    // Set selected color in the form
    useEffect(() => {
        if (!isVariant) {
            return;
        }

        const filteredVariantTraits = useFilteredVariantTraits(variantTraits, variantChildren, variantSelection, true);
        const colorTrait = filteredVariantTraits?.find(t => t.name.toLowerCase() === "color");

        colorTrait && setTraitValues(colorTrait.traitValues || []);

        const selected =
            colorTrait?.traitValues?.find(v => v.id === variantSelection[colorTrait.id])?.valueDisplay ?? "";

        setFieldValue("Color", selected);
    }, [variantSelection, variantChildren, variantTraits]);

    const submitHandler = async (fieldValues: QuickRfqApiParameter, setSubmitting: any) => {
        setSubmitting(true);
        if (Object.keys(errors).length > 0) {
            setSubmitting(false);
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "PdpRfq", recaptchaRef });

        if (!isReCaptchaValid) {
            setSubmitting(false);
            return;
        }

        SubmitQuickRfqForm({
            ...fieldValues,
            Length: Number(fieldValues?.["Length"]) as any,
            IsExpedited: isExpedited,
            onSuccess: () => {
                setSubmitting(false);
                setErrorMessage("");
                requestQuoteEvent && trackHubspotRfqGa4Events();
                thankYouUrl && history.push(thankYouUrl);
            },
            onError: (error: string) => {
                setSubmitting(false);
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const handleUploadFileChange = (e: ChangeEvent<AnyStyledComponent>) => {
        const reader = new FileReader();
        const attachmentFile = e.target.files?.[0];
        setFile(attachmentFile);
        reader.onload = () => {
            const base64DataUrl = reader.result as string;
            const base64Data = base64DataUrl?.split(",")[1];

            setFieldValue("AttachmentBase64", base64Data);
            setFieldValue("AttachmentFileName", attachmentFile.name);
            setFieldValue("AttachmentMimeType", attachmentFile.type);
        };
        reader.readAsDataURL(attachmentFile);
    };

    const dateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setdate(selectedDay);
        setFieldValue("RequiredBy", convertDateToDateOnlyString(selectedDay));
    };

    return (
        <StyledForm onSubmit={handleSubmit} noValidate data-test-selector="quickRfqFrom">
            <Typography {...styles.title}>
                {translate(`${isExpedited ? "Request an Expedited Order" : "Submit a Quick RFQ"}`)}
            </Typography>
            {!isExpedited && (
                <Typography {...styles.subTitle}>{translate("For quantity discounts and lower prices -")}</Typography>
            )}
            <StyledWrapper {...styles.formWrapper}>
                <StyledWrapper {...styles.inputsWrapper}>
                    <TextField
                        {...field("Length")}
                        label={translate("Length (ft) / Quantity")}
                        error={field("Length").error && "Length (ft) / Quantity is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true, validateAs: "number" }}
                    />

                    {isVariant && (
                        <Select
                            {...field("Color")}
                            label={translate("Color")}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="" disabled>
                                {translate("Select Color")}
                            </option>
                            {traitValues?.map((o: any) => (
                                <option key={o.id} value={o.valueDisplay}>
                                    {o.valueDisplay}
                                </option>
                            ))}
                        </Select>
                    )}

                    {isExpedited && (
                        <DatePicker
                            label={translate("Required Date")}
                            selectedDay={date}
                            dateTimePickerProps={{ minDate: new Date() }}
                            onDayChange={dateChangeHandler}
                            required={true}
                            disabled={field("RequiredBy").disabled}
                            error={field("RequiredBy").error && "Required Date is a required field"}
                            {...{ showRequiredStar: true }}
                        />
                    )}

                    <TextField
                        {...field("FirstName")}
                        label={translate("First name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("LastName")}
                        label={translate("Last Name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("Email")}
                        type="email"
                        label={translate("Email")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("Company")}
                        label={translate("Company name")}
                        error={field("Company").error && "Company name is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("Phone")}
                        type="phone"
                        label={translate("Phone number")}
                        error={field("Phone").error && "Phone number is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    {countries && countries.length > 1 && (
                        <Select
                            {...field("CountryCode")}
                            label={translate("Country")}
                            onChange={(e: any) => {
                                const key = e.target.name;
                                const oldValue = values[key];
                                const newValue = e.target.value;
                                const masked = applyMask(masksList[key], newValue, oldValue);
                                setFieldValue(key, masked);
                                // XNG-Changes
                                const selectedCountry = countries?.find(c => c.abbreviation === masked);
                                setCountry(selectedCountry);
                                if (!selectedCountry?.states?.length) {
                                    setFieldValue("State", "");
                                } else if (selectedCountry.states.length === 1) {
                                    setFieldValue("State", selectedCountry.states[0].abbreviation);
                                }
                            }}
                            error={field("CountryCode").error && "Country is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {countries?.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}

                    {country && (
                        <TextField
                            {...field("StreetAddress")}
                            label={translate("Street address")}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        />
                    )}
                    {country && (
                        <TextField
                            {...field("City")}
                            label={translate("City")}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        />
                    )}

                    {country && country?.states && !!country.states?.length && (
                        <Select
                            {...field("State")}
                            label={translate("State/Region")}
                            error={field("State").error && "State/Region is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="" disabled>
                                {translate("Select State")}
                            </option>
                            {country.states.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}

                    {country && (
                        <TextField
                            {...field("PostalCode")}
                            label={translate("Postal Code")}
                            type={
                                country?.abbreviation?.toLowerCase() === "ca"
                                    ? "postalCodeCA"
                                    : country?.abbreviation?.toLowerCase() === "us"
                                    ? "postalCodeUS"
                                    : "text"
                            }
                            required={true}
                            {...{ displayStarForRequired: true }}
                        />
                    )}

                    <TextArea
                        {...field("Notes")}
                        label={translate("Comments")}
                        onChange={e => setFieldValue("Notes", e.target.value)}
                        error={field("Notes").error && "Comments is a required field"}
                    />

                    <StyledWrapper {...styles.fileUpload}>
                        <FileUpload
                            onFileChange={e => {
                                handleUploadFileChange(e);
                            }}
                            inputRef={fileUploadRef}
                            fileName={file?.name || ""}
                            labelPosition="top"
                            uid="quickRfqForm"
                            buttonText={translate("Choose File")}
                            label={translate("Upload Attachment")}
                            buttonProps={{
                                variant: "tertiary",
                            }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                {errorMessage && (
                    <Notify type="danger" data-test-selector="quickRfqFrom_ErrorMessage">
                        {errorMessage}
                    </Notify>
                )}
                <ReCaptcha
                    recaptchaRef={recaptchaRef}
                    location="PdpRfq"
                    {...{ forceEnable: reCaptchaEnabledForQuickRfq }}
                    {...{ displayForAuthenticated: true }}
                />
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    {...styles.submitButton}
                    data-test-selector="submit_quick_rfq_form"
                >
                    {translate(`${isExpedited ? "Submit Expedite Request" : "Submit RFQ"}`)}
                </Button>
            </StyledWrapper>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(withParentProductId(connect(mapStateToProps, mapDispatchToProps)(QuickRfqFrom))),
    definition: {
        displayName: "Quick Rfq From",
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
